import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { supabase } from '../services/supabase';
import { thumbUrl } from '../utils/imageUrl';

const CITIES = ['Bakı', 'Sumqayıt', 'Gəncə', 'Lənkəran', 'Şirvan', 'Naxçıvan'];

const PACKAGES = [
  {
    tier: 0, name: 'Free', price: '0₼/ay', color: '#6b7280',
    features: ['App-da görünüş', '5 şəkil', 'Rezervasiya', 'Doluluq göstəricisi'],
    limit: 'Əsas imkanlar',
  },
  {
    tier: 1, name: 'Starter', price: '75₼/ay', color: '#3b82f6',
    features: ['Free + hər şey', '10 şəkil', 'Kampaniyalar', 'Flash booking', 'Statistika'],
    limit: 'Kiçik filiallar üçün',
  },
  {
    tier: 2, name: 'Growth', price: '110₼/ay', color: '#8b5cf6',
    features: ['Starter + hər şey', '30 şəkil', 'Featured sıralama', 'QR Skan', 'Prioritet dəstək'],
    limit: 'Böyüyən filiallar üçün',
  },
  {
    tier: 3, name: 'Premium', price: '150₼/ay', color: '#f97316',
    features: ['Growth + hər şey', 'Limitsiz şəkil', 'Verified Partner ✦', 'Top sıralama', 'VIP dəstək'],
    limit: 'Ən yüksək görünürlük',
  },
];

const emptyForm = () => ({
  // Step 0 — Filial məlumatları
  name: '', city: 'Bakı', address: '', phone: '', hours: '',
  // Step 1 — Paket
  premium_tier: 0,
  // Step 2 — Menecer
  mgr_email: '', mgr_password: '', mgr_confirm: '',
});

const STEP_LABELS = ['📍 Filial', '💳 Paket', '👤 Menecer'];

export default function LocationsPage() {
  const { business } = useAuth();
  const { locations, refreshLocations, isOwner } = useLocation();

  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formStep, setFormStep] = useState(0);
  const [form, setForm] = useState(emptyForm());
  const [message, setMessage] = useState('');

  const [managers, setManagers] = useState([]);

  useEffect(() => { loadManagers(); }, [business]);

  const showMsg = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 4000); };

  const loadManagers = async () => {
    if (!business?.id) return;
    const { data } = await supabase
      .from('business_users')
      .select('*, business_locations(name)')
      .eq('business_id', business.id)
      .order('invited_at', { ascending: false });
    setManagers(data || []);
  };

  const closeForm = () => { setShowForm(false); setForm(emptyForm()); setFormStep(0); };

  const handleNextStep = () => {
    if (formStep === 0 && !form.name.trim()) { showMsg('❌ Filial adı tələb olunur'); return; }
    if (formStep === 0 && !form.address.trim()) { showMsg('❌ Ünvan tələb olunur'); return; }
    setFormStep(s => s + 1);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (formStep < 2) { handleNextStep(); return; }

    // Step 2 validasiya
    if (!form.mgr_email.trim()) { showMsg('❌ Menecer emaili tələb olunur'); return; }
    if (form.mgr_password.length < 6) { showMsg('❌ Şifrə ən az 6 simvol olmalıdır'); return; }
    if (form.mgr_password !== form.mgr_confirm) { showMsg('❌ Şifrələr uyğun gəlmir'); return; }

    setSaving(true);
    try {
      // 1. Filial yarat
      const { data: loc, error: locErr } = await supabase
        .from('business_locations')
        .insert([{
          business_id: business.id,
          name: form.name,
          address: form.address,
          city: form.city,
          phone: form.phone || null,
          hours: form.hours || null,
          is_main: false,
          is_open: true,
          business_type: business.business_type || 'cafe',
          premium_tier: form.premium_tier,
        }])
        .select()
        .single();
      if (locErr) throw locErr;

      // 2. Manager auth hesabı yarat
      // Owner sessiyanı saxla → manager yarat → sessiyanı bərpa et
      const { data: { session: ownerSession } } = await supabase.auth.getSession();

      const { data: signUpData, error: signUpErr } = await supabase.auth.signUp({
        email: form.mgr_email.trim().toLowerCase(),
        password: form.mgr_password,
        options: {
          data: { account_type: 'business_manager' },
          emailRedirectTo: 'https://business.cafegoo.com/login',
        },
      });

      if (signUpErr) {
        await supabase.from('business_locations').delete().eq('id', loc.id);
        throw signUpErr;
      }

      // Manager auto-signin oldu — sign out edib owner sessiyanı geri yüklə
      await supabase.auth.signOut();
      if (ownerSession?.access_token) {
        await supabase.auth.setSession({
          access_token: ownerSession.access_token,
          refresh_token: ownerSession.refresh_token,
        });
      }

      const authUser = signUpData.user;

      // 3. business_users cədvəlinə əlavə et
      await supabase.from('business_users').insert([{
        business_id: business.id,
        location_id: loc.id,
        auth_user_id: authUser.id,
        user_email: form.mgr_email.trim().toLowerCase(),
        role: 'manager',
        accepted_at: new Date().toISOString(),
      }]);

      showMsg(`✅ Filial yaradıldı! Menecer: ${form.mgr_email} / ${form.mgr_password}`);
      closeForm();
      refreshLocations();
      loadManagers();
    } catch (err) {
      showMsg('❌ ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu filialı silmək istəyirsiniz?')) return;
    const { error } = await supabase.from('business_locations').delete().eq('id', id);
    if (error) { showMsg('❌ ' + error.message); return; }
    showMsg('✅ Filial silindi');
    refreshLocations();
  };

  const handleRemoveManager = async (id) => {
    if (!window.confirm('Bu istifadəçini silmək istəyirsiniz?')) return;
    await supabase.from('business_users').delete().eq('id', id);
    loadManagers();
  };

  return (
    <div style={s.page}>
      <div style={s.topRow}>
        <div>
          <h1 style={s.title}>Filiallar</h1>
          <p style={s.sub}>Bütün lokasiyalarınızı bir yerdən idarə edin</p>
        </div>
        {isOwner && (
          business?.is_chain ? (
            <button style={s.addBtn} onClick={() => { setForm(emptyForm()); setShowForm(true); }}>
              + Filial Əlavə Et
            </button>
          ) : (
            <div style={s.lockedBadge}>
              🔒 Yalnız Chain hesabı
            </div>
          )
        )}
      </div>

      {message && (
        <div style={{ ...s.msg, background: message.startsWith('❌') ? '#fee2e2' : '#d1fae5', color: message.startsWith('❌') ? '#b91c1c' : '#065f46' }}>
          {message}
        </div>
      )}

      {/* Locked notice for non-chain */}
      {isOwner && !business?.is_chain && (
        <div style={s.lockedNotice}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>🔒</div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e', marginBottom: 6 }}>Filial yaratmaq mümkün deyil</div>
          <p style={{ margin: 0, color: '#666', fontSize: 13, maxWidth: 400 }}>
            Bu hesab tək kafe kimi qeydiyyatdan keçib. Filial əlavə etmək üçün hesabınızı <b>Chain</b> hesabına yüksəldin.
          </p>
          <div style={{ marginTop: 14, fontSize: 12, color: '#f97316', fontWeight: 600 }}>
            Yüksəltmək üçün dəstəklə əlaqə saxlayın →
          </div>
        </div>
      )}

      {/* Branch list */}
      {locations.filter(l => !l.is_main).length === 0 && !showForm ? (
        <div style={s.empty}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>📍</div>
          <p style={{ color: '#888', margin: 0 }}>Hələ əlavə filial yoxdur.</p>
        </div>
      ) : (
        locations.filter(l => !l.is_main).map(loc => {
          const avail = (loc.total_capacity || 0) - (loc.current_occupancy || 0);
          const pct = loc.total_capacity ? Math.round((loc.current_occupancy || 0) / loc.total_capacity * 100) : 0;
          const barColor = pct >= 80 ? '#ef4444' : pct >= 50 ? '#f59e0b' : '#22c55e';
          return (
            <div key={loc.id} style={s.card}>
              <div style={s.cardRow}>
                {loc.image_url && (
                  <img src={thumbUrl(loc.image_url)} alt="" style={{ width: 72, height: 72, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} onError={e => e.target.style.display='none'} />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <div style={s.locName}>{loc.name}</div>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                      background: loc.is_open ? '#dcfce7' : '#fee2e2',
                      color: loc.is_open ? '#166534' : '#b91c1c' }}>
                      {loc.is_open ? 'Açıq' : 'Bağlı'}
                    </span>
                  </div>
                  <div style={s.locAddr}>{loc.address}, {loc.city}</div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
                    {loc.phone && <span style={s.locDetail}>📞 {loc.phone}</span>}
                    {loc.hours && <span style={s.locDetail}>🕐 {loc.hours}</span>}
                    {loc.cuisine && <span style={s.locDetail}>🍽️ {loc.cuisine}</span>}
                    {loc.price_range && <span style={s.locDetail}>{loc.price_range}</span>}
                  </div>
                  {loc.total_capacity > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#888', marginBottom: 3 }}>
                        <span>Doluluq</span>
                        <span>{loc.current_occupancy || 0} / {loc.total_capacity} nəfər · {avail} boş</span>
                      </div>
                      <div style={{ height: 6, borderRadius: 3, background: '#f1f5f9', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 3, transition: '0.3s' }} />
                      </div>
                    </div>
                  )}
                  {loc.features?.length > 0 && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                      {loc.features.slice(0, 4).map(f => (
                        <span key={f} style={{ fontSize: 10, color: '#666', background: '#f5f5f5', borderRadius: 10, padding: '2px 7px' }}>{f}</span>
                      ))}
                      {loc.features.length > 4 && <span style={{ fontSize: 10, color: '#aaa' }}>+{loc.features.length - 4}</span>}
                    </div>
                  )}
                </div>
                {isOwner && (
                  <button style={s.deleteBtn} onClick={() => handleDelete(loc.id)}>Sil</button>
                )}
              </div>
            </div>
          );
        })
      )}

      {/* ── Multi-step Filial Formu ──────────────────────────────── */}
      {showForm && business?.is_chain && (
        <div style={s.formCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
              Yeni Filial Əlavə Et
            </h3>
            <button onClick={closeForm} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#aaa' }}>✕</button>
          </div>

          {/* Step indicator */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 24 }}>
            {STEP_LABELS.map((label, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 12,
                    background: i < formStep ? '#22c55e' : i === formStep ? '#f97316' : '#e5e7eb',
                    color: i <= formStep ? '#fff' : '#999',
                  }}>
                    {i < formStep ? '✓' : i + 1}
                  </div>
                  <div style={{ fontSize: 11, color: i === formStep ? '#f97316' : '#aaa', marginTop: 4, whiteSpace: 'nowrap' }}>
                    {label}
                  </div>
                </div>
                {i < STEP_LABELS.length - 1 && (
                  <div style={{ height: 2, flex: 0.5, background: i < formStep ? '#22c55e' : '#e5e7eb', marginBottom: 16 }} />
                )}
              </div>
            ))}
          </div>

          <form onSubmit={handleSave}>
            {/* ── Step 0: Filial məlumatları ── */}
            {formStep === 0 && (
              <div style={s.grid}>
                <div style={s.field}>
                  <label style={s.label}>Filial Adı *</label>
                  <input style={s.input} value={form.name} placeholder="Nizami Filialı"
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Şəhər</label>
                  <select style={s.input} value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))}>
                    {CITIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ ...s.field, gridColumn: '1 / -1' }}>
                  <label style={s.label}>Ünvan *</label>
                  <input style={s.input} value={form.address} placeholder="Küçə adı, bina nömrəsi"
                    onChange={e => setForm(p => ({ ...p, address: e.target.value }))} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Telefon</label>
                  <input style={s.input} value={form.phone} placeholder="+994 50 000 00 00"
                    onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>İş saatları</label>
                  <input style={s.input} value={form.hours} placeholder="09:00–22:00"
                    onChange={e => setForm(p => ({ ...p, hours: e.target.value }))} />
                </div>
              </div>
            )}

            {/* ── Step 1: Paket seçimi ── */}
            {formStep === 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {PACKAGES.map(pkg => {
                  const selected = form.premium_tier === pkg.tier;
                  return (
                    <div key={pkg.tier} onClick={() => setForm(p => ({ ...p, premium_tier: pkg.tier }))}
                      style={{
                        border: `2px solid ${selected ? pkg.color : '#e5e7eb'}`,
                        borderRadius: 12, padding: '14px 16px', cursor: 'pointer',
                        background: selected ? `${pkg.color}10` : '#fafafa',
                        transition: '0.15s',
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 12, height: 12, borderRadius: '50%', background: pkg.color }} />
                          <span style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e' }}>{pkg.name}</span>
                          <span style={{ fontSize: 12, color: '#888' }}>{pkg.limit}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 800, fontSize: 15, color: pkg.color }}>{pkg.price}</span>
                          {selected && <span style={{ fontSize: 16 }}>✓</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {pkg.features.map(f => (
                          <span key={f} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: selected ? `${pkg.color}20` : '#f1f5f9', color: selected ? pkg.color : '#555', fontWeight: 500 }}>
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Step 2: Menecer hesabı ── */}
            {formStep === 2 && (
              <div style={s.grid}>
                <div style={{ ...s.field, gridColumn: '1 / -1' }}>
                  <div style={s.infoBox}>
                    Bu məlumatları menecerə verin. İlk girişdə şifrəsini dəyişdirəcək.
                  </div>
                </div>
                <div style={{ ...s.field, gridColumn: '1 / -1' }}>
                  <label style={s.label}>Menecer Email *</label>
                  <input style={s.input} type="email" value={form.mgr_email} placeholder="menecer@email.com"
                    onChange={e => setForm(p => ({ ...p, mgr_email: e.target.value }))} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Müvəqqəti Şifrə *</label>
                  <input style={s.input} type="text" value={form.mgr_password} placeholder="min. 6 simvol"
                    onChange={e => setForm(p => ({ ...p, mgr_password: e.target.value }))} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Şifrəni təsdiqlə *</label>
                  <input style={s.input} type="text" value={form.mgr_confirm} placeholder="eyni şifrəni yazın"
                    onChange={e => setForm(p => ({ ...p, mgr_confirm: e.target.value }))} />
                </div>
                {/* Xülasə */}
                <div style={{ ...s.field, gridColumn: '1 / -1' }}>
                  <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14, border: '1px solid #e5e7eb', fontSize: 13, color: '#555', lineHeight: 1.8 }}>
                    <b>Filial:</b> {form.name}<br />
                    <b>Ünvan:</b> {form.city}, {form.address}<br />
                    {form.phone && <><b>Tel:</b> {form.phone}<br /></>}
                    <b>Paket:</b> {PACKAGES.find(p => p.tier === form.premium_tier)?.name} — {PACKAGES.find(p => p.tier === form.premium_tier)?.price}
                  </div>
                </div>
              </div>
            )}

            {/* Navigation buttons */}
            <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'space-between' }}>
              <button type="button" style={s.cancelBtn}
                onClick={() => formStep > 0 ? setFormStep(s => s - 1) : closeForm()}>
                {formStep > 0 ? '← Geri' : 'Ləğv Et'}
              </button>
              {formStep < 2 ? (
                <button type="button" style={s.saveBtn} onClick={handleNextStep}>
                  İrəli →
                </button>
              ) : (
                <button type="submit" style={s.saveBtn} disabled={saving}>
                  {saving ? 'Yaradılır...' : '✓ Filial + Menecer Yarat'}
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {/* ── Menecerlər — yalnız owner görür ──────────────────────── */}
      {isOwner && <div style={s.sectionHeader}>
        <h2 style={s.sectionTitle}>👥 Filial Menecerləri</h2>
      </div>}

      {isOwner && (managers.length === 0 ? (
        <div style={{ ...s.empty, marginTop: 0 }}>
          <p style={{ color: '#aaa', margin: 0, fontSize: 14 }}>Hələ menecer əlavə edilməyib</p>
        </div>
      ) : (
        <div style={s.card}>
          {managers.map(m => (
            <div key={m.id} style={s.managerRow}>
              <div style={s.managerAvatar}>{m.user_email[0].toUpperCase()}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{m.user_email}</div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                  {m.business_locations?.name || 'Ana Filial'} · {m.role === 'manager' ? 'Menecer' : 'Staff'}
                  {m.accepted_at
                    ? <span style={{ color: '#22c55e', marginLeft: 8 }}>✓ Aktiv</span>
                    : <span style={{ color: '#f59e0b', marginLeft: 8 }}>⏳ Gözləyir</span>
                  }
                </div>
              </div>
              <button style={s.removeBtn} onClick={() => handleRemoveManager(m.id)}>✕</button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

const s = {
  page: { padding: '32px 24px', maxWidth: 820, margin: '0 auto' },
  topRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, gap: 16 },
  title: { margin: 0, fontSize: 22, fontWeight: 800, color: '#1a1a2e' },
  sub: { margin: '4px 0 0', color: '#888', fontSize: 14 },
  addBtn: { padding: '10px 20px', borderRadius: 10, border: 'none', background: '#f97316', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', flexShrink: 0 },
  msg: { padding: '10px 16px', borderRadius: 10, marginBottom: 16, fontSize: 14, fontWeight: 600 },
  card: { background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: '16px 20px', marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
  cardRow: { display: 'flex', alignItems: 'flex-start', gap: 16 },
  mainBadge: { fontSize: 10, fontWeight: 700, letterSpacing: 1, color: '#f97316', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, padding: '2px 8px', display: 'inline-block', marginBottom: 4 },
  locName: { fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 2 },
  locAddr: { fontSize: 13, color: '#666', marginBottom: 2 },
  locDetail: { fontSize: 12, color: '#888', marginTop: 2 },
  editBtn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#444', fontSize: 13, cursor: 'pointer' },
  deleteBtn: { padding: '6px 14px', borderRadius: 8, border: 'none', background: '#fee2e2', color: '#b91c1c', fontSize: 13, cursor: 'pointer' },
  empty: { textAlign: 'center', padding: '32px 20px', background: '#fafafa', borderRadius: 14, border: '1px dashed #e5e7eb', marginBottom: 16 },
  formCard: { background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: '22px', marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 12, fontWeight: 600, color: '#555' },
  input: { padding: '9px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, outline: 'none', background: '#fafafa' },
  saveBtn: { padding: '10px 24px', borderRadius: 10, border: 'none', background: '#f97316', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  cancelBtn: { padding: '10px 20px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', color: '#666', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '28px 0 14px' },
  sectionTitle: { margin: 0, fontSize: 16, fontWeight: 700, color: '#1a1a2e' },
  infoBox: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#1e40af', margin: '14px 0' },
  managerRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #f5f5f5' },
  managerAvatar: { width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 },
  removeBtn: { background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: 16, padding: 4 },
  lockedBadge: { padding: '8px 16px', borderRadius: 10, border: '1.5px dashed #d1d5db', background: '#f9fafb', color: '#9ca3af', fontWeight: 600, fontSize: 13, flexShrink: 0 },
  lockedNotice: { textAlign: 'center', padding: '32px 24px', background: '#fff7ed', borderRadius: 14, border: '1.5px dashed #fed7aa', marginBottom: 20 },
};
